import React, { useState, useEffect } from 'react';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const STIMULUS_DURATION = 1250;
const FIXATION_DURATION = 750;
const TRIALS = 40;
const MATCHES_REQUIRED = 8;

const generateSequence = (length = TRIALS) => {
  const sequence = new Array(length);
  const matchIndices = [];

  while (matchIndices.length < MATCHES_REQUIRED) {
    const idx = Math.floor(Math.random() * (length - 2)) + 1;
    if (!matchIndices.includes(idx)) {
      matchIndices.push(idx);
    }
  }
  matchIndices.sort((a, b) => a - b);

  for (let i = 0; i < length; i++) {
    if (matchIndices.includes(i)) {
      sequence[i] = sequence[i - 1];
    } else {
      let letter;
      do {
        letter = LETTERS[Math.floor(Math.random() * LETTERS.length)];
      } while (i > 0 && letter === sequence[i - 1]);
      sequence[i] = letter;
    }
  }

  return sequence;
};

export default function OneBack({ onComplete }) {
  const [phase, setPhase] = useState('instructions');
  const [countdown, setCountdown] = useState(3);
  const [sequence] = useState(generateSequence());
  const [index, setIndex] = useState(0);
  const [trialPhase, setTrialPhase] = useState('fixation');
  const [stimulusTime, setStimulusTime] = useState(null);
  const [responses, setResponses] = useState([]);
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (phase === 'countdown') {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        setPhase('task');
        setTrialPhase('fixation');
      }
    }
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'task' || index >= sequence.length) return;

    if (trialPhase === 'fixation') {
      const timer = setTimeout(() => {
        setStimulusTime(Date.now());
        setClicked(false);
        setTrialPhase('stimulus');
      }, FIXATION_DURATION);
      return () => clearTimeout(timer);
    }

    if (trialPhase === 'stimulus') {
      const timer = setTimeout(() => {
        recordResponse(false, null);
      }, STIMULUS_DURATION);
      return () => clearTimeout(timer);
    }
  }, [phase, index, trialPhase]);

  useEffect(() => {
    if (phase === 'done' && responses.length > 0) {
      const correct = responses.filter(r => r.correct).length;
      const targets = responses.filter(r => r.isTarget).length;
      const falseAlarms = responses.filter(r => !r.isTarget && r.userClicked).length;
      const misses = responses.filter(r => r.isTarget && !r.userClicked).length;
      const avgRT =
        responses.filter(r => r.reactionTime !== null).reduce((sum, r) => sum + r.reactionTime, 0) /
        responses.filter(r => r.reactionTime !== null).length;

      if (onComplete) {
        onComplete({
          correct,
          totalTrials: responses.length,
          targets,
          falseAlarms,
          misses,
          avgRT
        });
      }
    }
  }, [phase, responses, onComplete]);

  const isTarget = () => {
    return index >= 1 && sequence[index] === sequence[index - 1];
  };

  const handleClick = () => {
    if (!clicked) {
      setClicked(true);
      recordResponse(true, Date.now());
    }
  };

  const recordResponse = (userClicked, clickTime) => {
    const rt = userClicked ? clickTime - stimulusTime : null;
    const correct = (isTarget() && userClicked) || (!isTarget() && !userClicked);

    const trial = {
      trial: index + 1,
      letter: sequence[index],
      isTarget: isTarget(),
      userClicked,
      correct,
      reactionTime: rt,
    };

    setResponses(prev => [...prev, trial]);

    if (index + 1 < sequence.length) {
      setIndex(index + 1);
      setTrialPhase('fixation');
    } else {
      setPhase('done');
    }
  };

  if (phase === 'done') {
    const correct = responses.filter(r => r.correct).length;
    const targets = responses.filter(r => r.isTarget).length;
    const falseAlarms = responses.filter(r => !r.isTarget && r.userClicked).length;
    const misses = responses.filter(r => r.isTarget && !r.userClicked).length;
    const avgRT =
      responses.filter(r => r.reactionTime !== null).reduce((sum, r) => sum + r.reactionTime, 0) /
      responses.filter(r => r.reactionTime !== null).length;

    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>1-Back Task Complete</h2>
        <p>Correct: {correct} / {responses.length}</p>
        <p>Targets: {targets}</p>
        <p>False Alarms: {falseAlarms}</p>
        <p>Misses: {misses}</p>
        <p>Avg RT: {Math.round(avgRT)} ms</p>
      </div>
    );
  }

  if (phase === 'instructions') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Welcome to the 1-Back Task</h2>
        <p>A letter will appear every 1.5 seconds.</p>
        <p>Click ONLY when the letter matches the one shown immediately before.</p>
        <button onClick={() => setPhase('countdown')}>Start</button>
      </div>
    );
  }

  if (phase === 'countdown') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <h2>Get Ready</h2>
        <p>Starting in... {countdown}</p>
      </div>
    );
  }

  if (phase === 'task' && trialPhase === 'fixation') {
    return (
      <div style={{ textAlign: 'center', marginTop: '3rem' }}>
        <div style={{ fontSize: '5rem' }}>+</div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', marginTop: '3rem' }}>
      <h2>Trial {index + 1} / {sequence.length}</h2>
      <div style={{ fontSize: '6rem' }}>{sequence[index]}</div>
      <p>Click if this matches the letter shown immediately before</p>
      <button onClick={handleClick} disabled={clicked}>
        Match
      </button>
    </div>
  );
}
